import React, { useState } from 'react';
import {
  AlertTriangle,
  ChevronRight,
  FileText,
  Hash,
  TrendingUp,
  Truck,
} from 'lucide-react';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { ShellGameDetection } from '../ShellGameDetection';
import type { Vehicle } from '../../../types/api';
import type { ObservedUnitsData, ShellGameAlert } from '../CarrierTabs.types';

export interface FleetProps {
  vehicles: Vehicle[];
  observedUnits: ObservedUnitsData | null;
  setSelectedVin: (vin: string | null) => void;
  setTimelineVin: (vin: string | null) => void;
  shellGameAlerts?: ShellGameAlert[];
  checkingShellGame?: boolean;
  hasCheckedShellGame?: boolean;
  loadCarrier: (dot: number) => void;
  checkShellGame: () => void;
}

const Fleet: React.FC<FleetProps> = ({
  vehicles,
  observedUnits,
  setSelectedVin,
  setTimelineVin,
  shellGameAlerts = [],
  checkingShellGame = false,
  hasCheckedShellGame = false,
  loadCarrier,
  checkShellGame,
}) => {
  // Local state for carrier navigation (not VIN timeline - that's handled by parent)
  const [, setSearchedDot] = useState<number | null>(null);

  // Pagination for vehicle list
  const [vehicleDisplayLimit, setVehicleDisplayLimit] = useState(50);

  // Shell game refresh handler
  const handleRefreshShellGameCheck = () => {
    checkShellGame();
  };

  // Batch progress (for UI display during shell game check)
  const batchProgress = { current: 0, total: 0 };
  const lastShellGameCheck: Date | null = null;

  // Background processing indicator (tied to shell game check)
  const backgroundProcessing = checkingShellGame;

  // Rental VINs lookup (empty by default - could be populated from API)
  const rentalVins: Record<string, boolean> = {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xl)' }}>
      {/* Shell Game Detection Component */}
      <ShellGameDetection
        alerts={shellGameAlerts as any}
        checkingShellGame={checkingShellGame}
        hasCheckedShellGame={hasCheckedShellGame}
        vehicleCount={vehicles.length}
        validVinCount={vehicles.filter(v => v.vinStatus === 'VALID').length}
        batchProgress={batchProgress}
        lastCheckTime={lastShellGameCheck}
        onViewTimeline={setTimelineVin}
        onNavigateToCarrier={(dot) => {
          setSearchedDot(dot);
          loadCarrier(dot);
        }}
        onRefresh={handleRefreshShellGameCheck}
      />

      {/* SECTION 3: Fleet summary and VIN list - Info Banner */}
      <div className="card-modern fade-in-up" style={{ borderLeft: '4px solid var(--color-primary)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'flex-start' }}>
          <Hash style={{ width: 24, height: 24, color: 'var(--color-primary)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <h4 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: 'var(--space-sm)' }}>
              Complete Fleet Intelligence - {(observedUnits?.summary?.totalUniqueVins || vehicles.length) as React.ReactNode} VINs
            </h4>
            <p className="text-muted" style={{ marginBottom: 'var(--space-md)' }}>
              Complete vehicle intelligence from NHTSA database and inspection records. Includes make/model details, crash history, unit type breakdown, and multi-carrier pattern detection
              Multi-carrier pattern detection runs automatically - NO COMPETITOR HAS THIS.
            </p>
            <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
              <div className="badge-modern badge-success">
                {vehicles.filter(v => v.vinStatus === 'VALID').length} Valid VINs
              </div>
              {vehicles.some(v => v.vinStatus !== 'VALID') && (
                <div className="badge-modern badge-warning">
                  {vehicles.filter(v => v.vinStatus !== 'VALID').length} Partial/Invalid
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Background Processing Indicator */}
      {backgroundProcessing && (
        <div className="card-modern fade-in-up" style={{ background: '#e0f2fe', border: '1px solid #0ea5e9' }}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
            <LoadingSpinner message="Decoding VINs and checking for fraud patterns..." size="sm" />
            <div>
              <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>
                Background Processing
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-700)' }}>
                Decoding VINs and analyzing carrier patterns across database...
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fleet Summary Stats */}
      {observedUnits?.summary && (
        <div className="grid-spacious fade-in-up" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', animationDelay: '0.15s' }}>
          <div className="stat-box">
            <div className="stat-value" style={{ fontSize: '1.25rem', color: 'var(--color-primary)' }}>
              {observedUnits.summary.totalUniqueVins as React.ReactNode}
            </div>
            <div className="stat-label">Total VINs</div>
          </div>
          <div className="stat-box">
            <Truck style={{ width: 24, height: 24, color: 'var(--color-gray-400)', margin: '0 auto var(--space-sm)' }} />
            <div className="stat-value" style={{ fontSize: '1.25rem' }}>
              {observedUnits.summary.truckCount as React.ReactNode}
            </div>
            <div className="stat-label">Power Units</div>
          </div>
          <div className="stat-box">
            <Truck style={{ width: 24, height: 24, color: 'var(--color-gray-400)', margin: '0 auto var(--space-sm)' }} />
            <div className="stat-value" style={{ fontSize: '1.25rem' }}>
              {observedUnits.summary.trailerCount as React.ReactNode}
            </div>
            <div className="stat-label">Trailers</div>
          </div>
          {(observedUnits.summary.otherCount as number) > 0 && (
            <div className="stat-box">
              <Truck style={{ width: 24, height: 24, color: 'var(--color-gray-400)', margin: '0 auto var(--space-sm)' }} />
              <div className="stat-value" style={{ fontSize: '1.25rem' }}>
                {observedUnits.summary.otherCount as React.ReactNode}
              </div>
              <div className="stat-label">Other</div>
            </div>
          )}
        </div>
      )}

      {/* Fleet by Manufacturer */}
      {observedUnits?.distribution?.byMake && observedUnits.distribution.byMake.length > 0 && (
        <div className="card-modern fade-in-up" style={{ animationDelay: '0.2s' }}>
          <h3 className="heading-3" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-lg)' }}>
            <TrendingUp size={20} style={{ color: 'var(--color-primary)' }} />
            Fleet by Manufacturer
          </h3>
          <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
            {observedUnits.distribution.byMake.slice(0, 8).map((item, idx) => (
              <div key={item.make} className="stat-box">
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                  {item.count}
                </div>
                <div className="stat-label" style={{ marginBottom: 'var(--space-sm)' }}>{item.make}</div>
                <div className="badge-modern badge-neutral" style={{ fontSize: '0.75rem' }}>
                  {(item as any).percentage}%
                </div>
              </div>
            ))}
          </div>
          {observedUnits.distribution.byMake.length > 8 && (
            <p className="text-muted" style={{ marginTop: 'var(--space-md)', fontSize: '0.875rem', textAlign: 'center' }}>
              + {observedUnits.distribution.byMake.length - 8} more manufacturers
            </p>
          )}
        </div>
      )}


      {vehicles.length > 0 ? (
        <>
          {/* Show message if many vehicles */}
          {vehicles.length > 100 && (
            <div className="card-modern fade-in-up" style={{
              background: '#fef3c7',
              borderLeft: '4px solid #f59e0b',
              marginBottom: 'var(--space-lg)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                <AlertTriangle size={20} style={{ color: '#d97706' }} />
                <div>
                  <div style={{ fontWeight: 600, color: '#92400e' }}>
                    Large Fleet: {vehicles.length} VINs
                  </div>
                  <div style={{ fontSize: '0.875rem', color: '#78350f' }}>
                    Showing first 50 VINs for performance. Use search or filters to find specific VINs.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid-spacious" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {/* Limit display for performance */}
            {vehicles.slice(0, vehicleDisplayLimit).map((vehicle, idx) => (
            <div
              key={vehicle.vin || idx}
              className="card-modern fade-in-up"
              style={{
                display: 'flex',
                flexDirection: 'column',
                animationDelay: `${idx * 0.03}s`
              }}
            >
              {/* VIN Header */}
              <div style={{ marginBottom: 'var(--space-lg)', paddingBottom: 'var(--space-lg)', borderBottom: '1px solid var(--color-gray-200)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                  <Hash size={16} style={{ color: 'var(--color-primary)' }} />
                  <span className="text-label" style={{ fontSize: '0.75rem' }}>VIN NUMBER</span>
                  {/* Check if this VIN is a rental unit */}
                  {rentalVins[vehicle.vin] && (
                    <span className="badge-modern badge-info" style={{
                      fontSize: '0.7rem',
                      padding: '0.25rem 0.5rem',
                      background: '#dbeafe',
                      color: '#1e40af',
                      border: '1px solid #93c5fd'
                    }}>
                      🚙 RENTAL UNIT
                    </span>
                  )}
                </div>
                <code style={{
                  display: 'block',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: 'var(--color-primary)',
                  background: '#dbeafe',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-md)',
                  wordBreak: 'break-all'
                }}>
                  {vehicle.vin}
                </code>
              </div>

              {/* Vehicle Details */}
              {vehicle.isDecoded ? (
                <div style={{ marginBottom: 'var(--space-lg)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                    <Truck size={20} style={{ color: 'var(--color-primary)' }} />
                    <div>
                      <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                        {vehicle.modelYear} {vehicle.make}
                      </div>
                      <div className="text-muted" style={{ fontSize: '0.875rem' }}>{vehicle.model}</div>
                    </div>
                  </div>
                  {vehicle.bodyClass && (
                    <span className="badge-modern badge-neutral" style={{ fontSize: '0.75rem' }}>
                      {vehicle.bodyClass}
                    </span>
                  )}
                </div>
              ) : null}

              {/* Crash Metrics */}
              <div className="grid-spacious" style={{
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 'var(--space-md)',
                marginBottom: 'var(--space-lg)'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-900)' }}>
                    {vehicle.crashCount || 0}
                  </div>
                  <div className="stat-label" style={{ fontSize: '0.75rem' }}>Crashes</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-danger)' }}>
                    {vehicle.totalFatalities || 0}
                  </div>
                  <div className="stat-label" style={{ fontSize: '0.75rem' }}>Fatal</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)' }}>
                    {vehicle.totalInjuries || 0}
                  </div>
                  <div className="stat-label" style={{ fontSize: '0.75rem' }}>Injuries</div>
                </div>
              </div>

              {/* Last Crash Info */}
              {vehicle.lastCrashDate && (
                <div style={{
                  marginBottom: 'var(--space-lg)',
                  padding: 'var(--space-md)',
                  background: '#fef3c7',
                  borderRadius: 'var(--radius-md)'
                }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-gray-700)' }}>
                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-xs)' }}>Last Crash:</div>
                    <div>{new Date(vehicle.lastCrashDate).toLocaleDateString()} in {vehicle.lastCrashState}</div>
                    {vehicle.towAwayCount > 0 && (
                      <div style={{ color: '#991b1b', fontWeight: 600, marginTop: 'var(--space-xs)' }}>
                        {vehicle.towAwayCount} Tow-Away{vehicle.towAwayCount > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Track History Buttons */}
              <div style={{ marginTop: 'auto', display: 'flex', gap: 'var(--space-sm)' }}>
                <button
                  onClick={() => setTimelineVin(vehicle.vin)}
                  disabled={vehicle.vinStatus !== 'VALID'}
                  className="btn-primary"
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-sm)',
                    opacity: vehicle.vinStatus !== 'VALID' ? 0.5 : 1,
                    cursor: vehicle.vinStatus !== 'VALID' ? 'not-allowed' : 'pointer'
                  }}
                >
                  {vehicle.vinStatus === 'VALID' ? (
                    <>
                      🔍 Fraud Timeline
                      <ChevronRight size={16} />
                    </>
                  ) : (
                    'Invalid VIN'
                  )}
                </button>
                {vehicle.vinStatus === 'VALID' && (
                  <button
                    onClick={() => setSelectedVin(vehicle.vin)}
                    className="btn-secondary"
                    style={{
                      padding: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title="View crash/inspection details"
                  >
                    <FileText size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Load More button for vehicles */}
        {vehicles.length > vehicleDisplayLimit && (
          <div style={{ marginTop: 'var(--space-xl)', textAlign: 'center' }}>
            <button
              onClick={() => setVehicleDisplayLimit(prev => prev + 50)}
              className="btn-secondary"
              style={{
                padding: 'var(--space-md) var(--space-xl)',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Load More VINs ({vehicleDisplayLimit} of {vehicles.length} shown)
            </button>
          </div>
        )}
        </>
      ) : (
        <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-lg)' }}>
          <Truck style={{ width: 40, height: 40, color: 'var(--color-gray-300)', margin: '0 auto var(--space-lg)' }} />
          <p className="text-muted" style={{ fontSize: '1rem' }}>
            No vehicle data available. VINs are extracted from crash records.
          </p>
        </div>
      )}
    </div>
  );
};

export default Fleet;
